import { memo, useMemo } from 'react'
import { TableCell, TableRow, Typography, Stack, alpha, Box, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectPositionLtpsSignature } from '../../selectors/security'
import { getSecurityKey } from '../../util/security'
import { toLocaleStringIn } from '../../util/general'
import { GroupedPosition } from '../../reducer/positions.reducer'
import { getPositionSecurityKeys, parseLtpsSignature } from '../../util/positionLtpsSignature'
import { useStyles } from './styles'

type BasketExpandedProfitRowProps = {
    position: GroupedPosition
    borderColor: string | null
}

const BasketExpandedProfitRow: React.FC<BasketExpandedProfitRowProps> = ({ position, borderColor }) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const positionSecurityKeys = useMemo(() => getPositionSecurityKeys(position), [position])
    const ltpsSignature = useSelector(selectPositionLtpsSignature(positionSecurityKeys))

    const profitData = useMemo(() => {
        const { security, realizedProfit, children = [] } = position
        const ltpsByKey = new Map<string, number>()
        const ltps = parseLtpsSignature(ltpsSignature)
        positionSecurityKeys.forEach((key, i) => {
            ltpsByKey.set(key, ltps[i] ?? 0)
        })

        let unrealizedProfit = 0
        children.forEach((childPos) => {
            const childSecKey = getSecurityKey(childPos.security.exchange, childPos.security.code)
            const ltp = ltpsByKey.get(childSecKey) ?? 0
            unrealizedProfit += (ltp - childPos.avgPrice) * childPos.quantity
        })

        const unrealizedConverted = unrealizedProfit / (security.priceMultiplier ?? 100)
        const realizedConverted = realizedProfit / (security.priceMultiplier ?? 100)

        return {
            unrealized: unrealizedConverted,
            realized: realizedConverted,
            total: unrealizedConverted + realizedConverted,
        }
    }, [position, ltpsSignature, positionSecurityKeys])

    if (profitData.realized === 0 && profitData.unrealized === 0) {
        return null
    }

    return (
        <TableRow sx={{ position: 'relative' }}>
            <TableCell className={cx(classes.tableCell, classes.symbolCell)} colSpan={5} sx={{ paddingY: '2px !important', position: 'relative' }}>
                {borderColor && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '5px',
                            backgroundColor: borderColor,
                        }}
                    />
                )}
                <Stack direction="row" columnGap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                    {profitData.realized !== 0 && (
                        <Typography fontSize={'10px'} lineHeight={'1rem'} sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                            REALISED{' '}
                            <span
                                className={cx(
                                    profitData.realized > 0
                                        ? classes.profitColor
                                        : profitData.realized < 0
                                          ? classes.lossColor
                                          : undefined
                                )}
                                style={{ fontSize: '12px' }}
                            >
                                {toLocaleStringIn(profitData.realized)}
                            </span>
                        </Typography>
                    )}
                    {profitData.unrealized !== 0 && (
                        <Typography fontSize={'10px'} lineHeight={'1rem'} sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                            UNREALISED{' '}
                            <span
                                className={cx(
                                    profitData.unrealized > 0
                                        ? classes.profitColor
                                        : profitData.unrealized < 0
                                          ? classes.lossColor
                                          : undefined
                                )}
                                style={{ fontSize: '12px' }}
                            >
                                {toLocaleStringIn(profitData.unrealized)}
                            </span>
                        </Typography>
                    )}
                    <Typography fontSize={'10px'} lineHeight={'1rem'} sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                        TOTAL{' '}
                        <span
                            className={cx(
                                profitData.total > 0
                                    ? classes.profitColor
                                    : profitData.total < 0
                                      ? classes.lossColor
                                      : undefined
                            )}
                            style={{ fontSize: '12px' }}
                        >
                            {toLocaleStringIn(profitData.total)}
                        </span>
                    </Typography>
                </Stack>
            </TableCell>
        </TableRow>
    )
}

export default memo(BasketExpandedProfitRow)
